import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Button, Typography, Box, Dialog } from '@material-ui/core';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { getCompleted } from 'logic/cottage';
import PATHS from 'constants/paths';
import CircularProgress from 'components/shared/CircularProgress';
import AcceptTermsDialog from 'components/shared/AcceptTermsDialog';
import FormSteps from './FormSteps';
import BaseForm from './Forms/Base';
import TextsForm from './Forms/Texts';
import PositionForm from './Forms/Position';
import LocationForm from './Forms/Location';
import AmenitiesForm from './Forms/Amenities';
import PricingForm from './Forms/Pricing';
import CalendarForm from './Forms/Calendar';
import ImagesForm from './Forms/Images';
import VerifyDialog from './Forms/Verify';

const REQUIRED_STEPS = ['base', 'text', 'position', 'location', 'images', 'calendar', 'verify'];

const styles = makeStyles((theme) => ({
  button: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  text: {
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  dialog: {
    [theme.breakpoints.up('md')]: {
      minWidth: 640,
    },
  },
  dialogPaper: {
    overflow: 'hidden',
  },
  dialogPaperWide: {
    [theme.breakpoints.up('md')]: {
      maxWidth: 840,
    },
  },
}));

const DialogContent = ({ onSubmit, dialog, cottage, isLoading, onClose, onVerify }) => {
  if (dialog === 'base') {
    return (
      <BaseForm
        isNew={false}
        isLoading={isLoading}
        onClose={onClose}
        onSubmit={onSubmit}
        cottage={cottage}
      />
    );
  }

  if (dialog === 'text') {
    return (
      <TextsForm isLoading={isLoading} onClose={onClose} onSubmit={onSubmit} cottage={cottage} />
    );
  }

  if (dialog === 'position') {
    return (
      <PositionForm isLoading={isLoading} onClose={onClose} onSubmit={onSubmit} cottage={cottage} />
    );
  }

  if (dialog === 'location') {
    return (
      <LocationForm isLoading={isLoading} onClose={onClose} onSubmit={onSubmit} cottage={cottage} />
    );
  }

  if (dialog === 'amenities') {
    return (
      <AmenitiesForm
        isLoading={isLoading}
        onClose={onClose}
        onSubmit={onSubmit}
        cottage={cottage}
      />
    );
  }

  if (dialog === 'pricing') {
    return (
      <PricingForm isLoading={isLoading} onClose={onClose} onSubmit={onSubmit} cottage={cottage} />
    );
  }

  if (dialog === 'calendar') {
    return (
      <CalendarForm isLoading={isLoading} onClose={onClose} onSubmit={onSubmit} cottage={cottage} />
    );
  }

  if (dialog === 'images') {
    return (
      <ImagesForm isLoading={isLoading} onClose={onClose} onSubmit={onSubmit} cottage={cottage} />
    );
  }

  if (dialog === 'verify') {
    return (
      <VerifyDialog isLoading={isLoading} onClose={onClose} onVerify={onVerify} cottage={cottage} />
    );
  }

  return null;
};

DialogContent.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  dialog: PropTypes.string,
  cottage: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
};

const CottageEdit = ({
  dialog,
  cottage,
  user,
  isLoading,
  onSendForReview,
  updatingDialogs,
  onClose,
  onSubmit,
  onVerify,
}) => {
  const classes = styles();
  const history = useHistory();
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down('xs'));

  const [completedArray, setCompletedArray] = React.useState([]);
  const [isReady, setIsReady] = React.useState(false);
  const [acceptTermsOpen, setAcceptTermsOpen] = React.useState(false);

  React.useEffect(() => {
    setIsReady(REQUIRED_STEPS.every((step) => completedArray.includes(step)));
  }, [completedArray]);

  React.useEffect(() => {
    if (cottage && !isLoading) {
      const completed = getCompleted(cottage);

      const completedKeys = Object.keys(completed).filter((key) => completed[key]);

      setCompletedArray(completedKeys);
    }
  }, [cottage, isLoading]);

  const goToPayment = () => {
    if (isReady) {
      return history.push(PATHS.cottages.payment.replace(':cottageId', cottage.id));
    }

    return null;
  };

  const sendForReview = () => {
    if (isReady) {
      setAcceptTermsOpen(true);
    }
  };

  const sendForReviewConfirmed = () => {
    onSendForReview();
    setAcceptTermsOpen(false);
  };

  const disabledPayment = !!completedArray && !isReady;
  const disabledSendForReview =
    !!cottage.asked_for_review_at ||
    updatingDialogs.onSendForReview ||
    (!!completedArray && !isReady);

  return (
    <>
      <FormSteps
        completedArray={completedArray}
        requiredSteps={REQUIRED_STEPS}
        cottage={cottage}
        user={user}
        currentStep={dialog}
      />
      {isLoading && <CircularProgress />}
      {!isLoading && (
        <div>
          {cottage.commission === null && (
            <Typography className={classes.text} color="textSecondary">
              <FormattedMessage
                id="cottages.whenYouDone"
                defaultMessage="When you are done with your ad just click Continue"
              />
            </Typography>
          )}
          {cottage.commission === true && !cottage.asked_for_review_at && (
            <Typography className={classes.text} color="textSecondary">
              <FormattedMessage
                id="cottages.whenYouDoneComission"
                defaultMessage="When you are done with your ad just click Send for review"
              />
            </Typography>
          )}
          {!cottage.reviewed_at && cottage.asked_for_review_at && (
            <Typography className={classes.text} color="textSecondary">
              <FormattedMessage
                id="cottages.weWillReview"
                defaultMessage="We will review your ad as quick as we can, normally in 24 hours."
              />
            </Typography>
          )}
          <Box mt={2}>
            {cottage.commission === null && (
              <Button
                color="primary"
                variant="contained"
                className={classes.button}
                disabled={disabledPayment}
                onClick={goToPayment}
              >
                <FormattedMessage id="Continue" defaultMessage="Continue" />
              </Button>
            )}
            {cottage.commission === true &&
              cottage.verified_at &&
              !cottage.reviewed_at &&
              !cottage.asked_for_review_at && (
                <Button
                  color="primary"
                  variant="contained"
                  className={classes.button}
                  disabled={disabledSendForReview}
                  onClick={sendForReview}
                >
                  <FormattedMessage id="Send for review" defaultMessage="Send for review" />
                </Button>
              )}
            {cottage.commission === true &&
              !cottage.reviewed_at &&
              !!cottage.asked_for_review_at && (
                <Button disabled color="primary" variant="contained" className={classes.button}>
                  <FormattedMessage
                    id="You sent your ad for review"
                    defaultMessage="You sent your ad for review"
                  />
                </Button>
              )}
            <Button
              variant="contained"
              href={`/sv/stuga/${cottage.id}`}
              target="_blank"
              disabled={!isReady}
              className={classes.button}
            >
              <FormattedMessage id="Preview" defaultMessage="Preview" />
            </Button>
          </Box>
        </div>
      )}
      {!isLoading && !!dialog && (
        <Dialog
          fullScreen={fullscreen}
          fullWidth
          maxWidth={dialog === 'verify' ? 'md' : 'lg'}
          onClose={onClose}
          open={[
            'base',
            'text',
            'position',
            'location',
            'amenities',
            'pricing',
            'calendar',
            'images',
            'verify',
          ].includes(dialog)}
          classes={{
            paper: classNames(classes.dialogPaper, dialog === 'pricing' && classes.dialogPaperWide),
          }}
        >
          <DialogContent
            dialog={dialog}
            onSubmit={onSubmit}
            cottage={cottage}
            isLoading={isLoading || (!!dialog && updatingDialogs[dialog])}
            onClose={onClose}
            onVerify={onVerify}
          />
        </Dialog>
      )}
      <AcceptTermsDialog
        open={acceptTermsOpen}
        onClose={() => setAcceptTermsOpen(false)}
        onConfirm={sendForReviewConfirmed}
      />
    </>
  );
};

CottageEdit.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  dialog: PropTypes.string,
  cottage: PropTypes.shape({
    id: PropTypes.number,
    asked_for_review_at: PropTypes.string,
    commission: PropTypes.bool,
    reviewed_at: PropTypes.string,
    verified_at: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  onSendForReview: PropTypes.func.isRequired,
  updatingDialogs: PropTypes.shape({
    onSendForReview: PropTypes.bool,
  }),
};

export default CottageEdit;
